import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormattedDuration } from 'modules/notes/common';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { SmartDiaryEntry } from '../../type';
import { useSmartEntryUnselectState } from '../../hooks/useSmartEntryUnselectState';
import { useSmartDiaryTotalDuration } from '.';

type SmartDiaryTotalsReturnType = {
  checked: boolean;
  indeterminate: boolean;
  totalDuration: string;
  onChange: () => void;
  disable: boolean;
};

export const useGroupCheckState = (
  smartEntries: SmartDiaryEntry[]
): SmartDiaryTotalsReturnType => {
  const { setValue, getValues, watch } = useFormContext();
  const {
    addUnselectedEntries,
    removeUnselectedEntries
  } = useSmartEntryUnselectState();
  const { syncableEntryIds, notesMap, updatePendingIds } = useMemo(() => {
    const syncableEntries = smartEntries
      ? smartEntries.filter((smartEntry) => smartEntry.canBeSynced)
      : [];
    const syncableEntryIds = syncableEntries.map((node) => node.id);
    const updatePendingIds = syncableEntries
      .filter((node) => node.syncStatus === DiaryEntrySyncStatus.UpdatePending)
      .map((node) => node.id);

    const notesMap = syncableEntries.reduce(
      (acc, note) => ({ ...acc, [note.id]: note }),
      {} as Record<string, SmartDiaryEntry>
    );

    return { syncableEntryIds, notesMap, updatePendingIds };
  }, [smartEntries]);

  const formValues = watch(syncableEntryIds);
  const result = useMemo<Record<string, boolean>>(
    () =>
      syncableEntryIds.reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [syncableEntryIds, formValues]
  );

  const [checked, setChecked] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);

  const duration = useSmartDiaryTotalDuration(result, notesMap);
  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: duration
  });

  const onChange = useCallback(() => {
    if (indeterminate) {
      addUnselectedEntries(updatePendingIds);
      setChecked(false);
      setIndeterminate(false);
      Object.keys(result).forEach((key) =>
        setValue(key, false, { shouldDirty: true })
      );
      return;
    }
    setIndeterminate(false);
    Object.keys(result).forEach((key) =>
      setValue(key, !checked, { shouldDirty: true })
    );
    setChecked(!checked);
    !checked
      ? removeUnselectedEntries(updatePendingIds)
      : addUnselectedEntries(updatePendingIds);
  }, [
    addUnselectedEntries,
    checked,
    indeterminate,
    removeUnselectedEntries,
    result,
    setValue,
    updatePendingIds
  ]);

  useEffect(() => {
    if (syncableEntryIds.length) {
      setIndeterminate(false);
      const values: Record<string, boolean> = syncableEntryIds.reduce(
        (acc, id) => ({ ...acc, [id]: result[id] ?? false }),
        {} as Record<string, boolean>
      );
      const noteKeys = Object.keys(values);
      const checkedNotes = noteKeys.filter((key) => values[key]);
      if (checkedNotes.length === noteKeys.length) {
        setChecked(true);
        return;
      }
      if (checkedNotes.length > 0) {
        setIndeterminate(true);
        return;
      }
      setChecked(false);
    } else {
      setChecked(false);
    }
  }, [syncableEntryIds, getValues, result]);

  return useMemo(
    () => ({
      checked,
      indeterminate,
      onChange,
      totalDuration: formattedDuration,
      disable: syncableEntryIds.length <= 0
    }),
    [
      checked,
      indeterminate,
      onChange,
      syncableEntryIds.length,
      formattedDuration
    ]
  );
};
